<script lang="ts">
  // − value + control with large touch targets. Works in display units; the parent converts.
  import Icon from './Icon.svelte';

  interface Props {
    value: number;
    step: number;
    min: number;
    max: number;
    /** Text after the number, e.g. ":1" or " m". */
    suffix?: string;
    decimals?: number;
    label: string;
    onchange: (v: number) => void;
  }
  let { value, step, min, max, suffix = '', decimals = 0, label, onchange }: Props = $props();

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  // snap to the step grid so +/- from an odd value lands on round numbers
  const bump = (dir: 1 | -1) => {
    const snapped = dir > 0 ? Math.floor(value / step + 1e-9) * step + step : Math.ceil(value / step - 1e-9) * step - step;
    onchange(clamp(+snapped.toFixed(4)));
  };
  let editing = $state(false);
</script>

<div class="stepper" role="group" aria-label={label}>
  <button type="button" class="b" aria-label="Decrease {label}" disabled={value <= min} onclick={() => bump(-1)}><Icon name="minus" /></button>
  {#if editing}
    <!-- svelte-ignore a11y_autofocus -->
    <input
      type="number"
      inputmode="decimal"
      {min}
      {max}
      {step}
      value={+value.toFixed(decimals)}
      autofocus
      aria-label={label}
      onblur={(e) => {
        const v = parseFloat(e.currentTarget.value);
        if (Number.isFinite(v)) onchange(clamp(v));
        editing = false;
      }}
      onkeydown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
    />
  {:else}
    <button type="button" class="v" aria-label="{label}: {value.toFixed(decimals)}{suffix}. Tap to type a value" onclick={() => (editing = true)}>
      {value.toFixed(decimals)}<small>{suffix}</small>
    </button>
  {/if}
  <button type="button" class="b" aria-label="Increase {label}" disabled={value >= max} onclick={() => bump(1)}><Icon name="plus" /></button>
</div>

<style>
  .stepper {
    display: inline-flex;
    align-items: stretch;
    border: 1px solid var(--line);
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    height: 44px;
  }
  .b {
    width: 44px;
    border: none;
    background: #f1f5f9;
    display: grid;
    place-items: center;
    color: var(--fg);
  }
  .b:disabled {
    opacity: 0.35;
  }
  .b:active {
    background: #e2e8f0;
  }
  .v,
  input {
    min-width: 92px;
    border: none;
    background: #fff;
    font-size: 18px;
    font-weight: 650;
    text-align: center;
    font-variant-numeric: tabular-nums;
    padding: 0 6px;
  }
  .v small {
    font-size: 13px;
    font-weight: 500;
    color: var(--muted);
    margin-left: 2px;
  }
  input {
    width: 92px;
    border-radius: 0;
  }
</style>
